import { useState } from 'react';

import { Input } from '@/components/common/Input';
import { Select } from '@/components/common/Select';
import { Textarea } from '@/components/common/Textarea';
import { CoordinateDisplay } from '@/components/map/CoordinateDisplay';
import { MapPickerModal } from '@/components/map/MapPickerModal';
import { Button } from '@/components/common/Button';
import { US_STATES, ZONES, zoneForState } from '@/utils/property-utils';
import { homeStateCenter } from '@/utils/property-center';
import { getZoneDisplayName, getZoneDescription } from '@/utils/zone-utils';
import { reverseGeocode } from '@/services/geocoding-service';
import type { Property, CreatePropertyInput } from '@/types/property';

interface PropertyFormProps {
  initialValues?: Partial<Property>;
  defaultState?: string;
  onSubmit: (data: CreatePropertyInput) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

const ZONE_OPTIONS = ZONES.filter((z) => z.value !== '');

export function PropertyForm({
  initialValues,
  defaultState = '',
  onSubmit,
  onCancel,
  isSubmitting,
}: PropertyFormProps) {
  const [name, setName] = useState(initialValues?.name ?? '');
  const [state, setState] = useState(initialValues?.state ?? defaultState);
  const [zone, setZone] = useState(initialValues?.zone ?? '');
  const [coordinates, setCoordinates] = useState<{ lat: number; lng: number } | null>(() => {
    if (
      initialValues?.latitude !== undefined &&
      initialValues?.longitude !== undefined
    ) {
      return { lat: initialValues.latitude, lng: initialValues.longitude };
    }
    return null;
  });
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [notes, setNotes] = useState(initialValues?.notes ?? '');
  const [coordError, setCoordError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Whether the zone override dropdown is currently visible
  const [showZoneDropdown, setShowZoneDropdown] = useState(!initialValues?.zone);

  const isEditMode = Boolean(initialValues?.id);

  // Derived: suggested zone for the selected state
  const stateDefaultZone = zoneForState(state);

  // Derived: home-state center for the map picker — used when no coordinates are set yet.
  // Existing coords always win (coordinates ?? homeCenter); unmapped states yield undefined,
  // falling through to the modal's US-centroid default.
  const homeCenter = homeStateCenter(initialValues?.state ?? defaultState);

  function handleStateChange(newState: string) {
    setState(newState);

    // Auto-derive zone from new state
    const suggested = zoneForState(newState);
    setZone(suggested);
    // If state has no mapping, force dropdown open so user picks manually
    if (!suggested) {
      setShowZoneDropdown(true);
    }
  }

  function handleZoneOverrideChange(newZone: string) {
    setZone(newZone);
    if (newZone) {
      setShowZoneDropdown(false);
    }
  }

  function handleUseStateDefault() {
    const suggested = zoneForState(state);
    setZone(suggested);
    setShowZoneDropdown(false);
  }

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = 'Name is required';
    if (!state) next.state = 'State is required';
    if (!zone) next.zone = 'Select a planting zone';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    if (!coordinates) {
      setCoordError('Please pick a location on the map.');
      return;
    }

    setCoordError(null);
    await onSubmit({
      name: name.trim(),
      state,
      zone,
      latitude: coordinates.lat,
      longitude: coordinates.lng,
      notes,
    });
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="flex flex-col gap-4">
      <Input
        id="property-name"
        label="Name"
        value={name}
        onChange={setName}
        placeholder="e.g., North 40"
        required
        error={errors.name}
      />

      <Select
        id="property-state"
        label="State"
        value={state}
        onChange={handleStateChange}
        options={US_STATES}
        required
        error={errors.state}
      />

      {/* Zone field — chip + description in normal mode, dropdown in override/empty mode */}
      <div className="flex flex-col gap-1.5" data-testid="zone-field">
        <span className="field-label">
          Zone <span className="text-[var(--color-accent)]">*</span>
        </span>

        {/* Show chip when zone is set and not in dropdown mode */}
        {zone && !showZoneDropdown && (
          <div className="flex flex-col gap-1.5" data-testid="zone-chip-area">
            <div className="flex items-center justify-between gap-3">
              <span
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[var(--color-elevated)] border border-[var(--color-border)] text-body-sm font-medium text-[var(--color-text-primary)] shrink-0"
                data-testid="zone-chip"
              >
                {getZoneDisplayName(zone)}
              </span>
              <button
                type="button"
                onClick={() => setShowZoneDropdown(true)}
                className="text-body-sm text-[var(--color-accent)] hover:opacity-80 transition-opacity shrink-0"
                data-testid="zone-change-btn"
              >
                Change
              </button>
            </div>
            {getZoneDescription(zone) && (
              <p className="text-body-xs text-[var(--color-text-muted)]">
                {getZoneDescription(zone)}
              </p>
            )}
          </div>
        )}

        {/* Dropdown: shown when zone is empty (unmapped state) or user clicked Change */}
        {(!zone || showZoneDropdown) && (
          <div className="flex flex-col gap-2" data-testid="zone-dropdown-area">
            {!zone && (
              <p className="text-body-xs text-[var(--color-text-muted)]">
                {state
                  ? `${state} doesn't have a default zone — select a planting zone for this property.`
                  : 'Select a planting zone for this property.'}
              </p>
            )}
            <select
              id="property-zone"
              value={zone}
              onChange={(e) => handleZoneOverrideChange(e.target.value)}
              className="bg-[var(--color-elevated)] rounded-md text-body-md text-[var(--color-text-primary)] px-3 py-2.5 w-full focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] focus:ring-offset-0"
              data-testid="zone-select"
            >
              <option value="" className="bg-[var(--color-elevated)]">
                Select a zone
              </option>
              {ZONE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value} className="bg-[var(--color-elevated)]">
                  {opt.label}
                </option>
              ))}
            </select>
            {/* Show "Use state default" link if state has a suggested zone */}
            {showZoneDropdown && stateDefaultZone && (
              <button
                type="button"
                onClick={handleUseStateDefault}
                className="text-body-xs text-[var(--color-accent)] hover:opacity-80 transition-opacity text-left"
                data-testid="zone-use-default-btn"
              >
                Use {getZoneDisplayName(stateDefaultZone)} (state default)
              </button>
            )}
          </div>
        )}

        {errors.zone && (
          <p className="text-body-xs text-[var(--color-accent)]">{errors.zone}</p>
        )}
      </div>

      <CoordinateDisplay
        lat={coordinates?.lat ?? null}
        lng={coordinates?.lng ?? null}
        onPickLocation={() => setMapPickerOpen(true)}
      />

      {coordError && (
        <p className="text-body-xs text-[var(--color-accent)] -mt-2">{coordError}</p>
      )}

      <MapPickerModal
        isOpen={mapPickerOpen}
        onClose={() => setMapPickerOpen(false)}
        onConfirm={(coords) => {
          setCoordinates(coords);
          setMapPickerOpen(false);
          setCoordError(null);
          // Auto-detect state from picked location
          reverseGeocode(coords.lat, coords.lng)
            .then((result) => {
              if (result?.state && !state) {
                handleStateChange(result.state);
              }
            })
            .catch(() => {});
        }}
        initialCoordinates={coordinates ?? homeCenter}
        initialStyle={coordinates ? 'satellite' : 'default'}
        title="Pick Property Location"
      />

      <Textarea
        id="property-notes"
        label="Notes"
        value={notes}
        onChange={setNotes}
        placeholder="Optional notes about this property..."
      />

      <div className="flex gap-2 justify-end pt-1">
        <Button variant="ghost" onClick={onCancel} type="button">
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting || !name.trim()}>
          {isEditMode ? 'Save Changes' : 'Add Property'}
        </Button>
      </div>
    </form>
  );
}
